import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BrainCircuit,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  Loader2,
  Send,
  Zap,
  Layers,
  Phone,
  FileText,
  ClipboardCheck,
  MessageSquare,
  ArrowRight,
  Repeat,
} from "lucide-react";

/* ── Master prompt ─────────────────────────────────────── */

const MASTER_PROMPT = "Prep tomorrow's schedule — verify insurance, update charts, and brief Dr. Sarah.";

/* ── Orchestration flow ────────────────────────────────── */
/*  The loop: Reasoning → Action (via specialist agent) → Observation, repeated */

const flowEntries = [
  {
    type: "reasoning",
    text: "8 appointments tomorrow. I'll start by verifying insurance benefits for every patient so nothing surprises us mid-day.",
    round: 1,
  },
  {
    type: "action",
    agent: "Amanda",
    role: "Insurance Verification",
    verb: "Verifying benefits for all 8 patients",
    color: "#3b82f6",
    icon: ShieldCheck,
    round: 1,
  },
  {
    type: "observation",
    text: "7 of 8 verified. Mrs. Chen's coverage last checked 18 months ago — portal data is stale.",
    round: 1,
  },
  {
    type: "reasoning",
    text: "Mrs. Chen is scheduled for a $2,400 crown tomorrow. Stale coverage means denial risk. I need live benefits straight from Delta.",
    round: 2,
  },
  {
    type: "action",
    agent: "Laura",
    role: "AI Insurance Caller",
    verb: "Calling Delta Dental for Mrs. Chen's crown eligibility",
    color: "#8b5cf6",
    icon: Phone,
    round: 2,
  },
  {
    type: "observation",
    text: "Rep confirmed coverage active. Full breakdown incoming via fax in ~30 min.",
    round: 2,
  },
  {
    type: "reasoning",
    text: "I can't wait passively for that fax — Joseph can monitor the queue and process it the moment it lands.",
    round: 3,
  },
  {
    type: "action",
    agent: "Joseph",
    role: "AI Fax Intelligence",
    verb: "Monitoring fax queue for Delta breakdown",
    color: "#f59e0b",
    icon: FileText,
    round: 3,
  },
  {
    type: "observation",
    text: "Fax received 14 min later. Parsed: D2740 covered at 50% after $50 deductible — matches our estimate.",
    round: 3,
  },
  {
    type: "reasoning",
    text: "Coverage confirmed and matches our treatment plan. Time to close the loop — update the chart and brief Dr. Sarah.",
    round: 4,
  },
  {
    type: "action",
    agent: "ARIA",
    role: "Chart + Comms",
    verb: "Updated Mrs. Chen's chart · SMS summary sent to Dr. Sarah",
    color: "#6366f1",
    icon: ClipboardCheck,
    round: 4,
  },
  {
    type: "success",
    text: "All 8 appointments prepped. Dr. Sarah's schedule is fully set — no surprises tomorrow.",
  },
];

const ENTRY_INTERVAL = 900;
const TYPING_SPEED = 28;

/* ── Value props ───────────────────────────────────────── */

const valueProps = [
  {
    icon: Repeat,
    title: "Iterative reason → act → observe loop",
    desc: "ARIA plans, dispatches a specialist agent, analyzes what comes back, and decides the next step — running the loop as many times as the job requires.",
    color: "bg-indigo-100 text-indigo-600 border-indigo-200",
  },
  {
    icon: Zap,
    title: "End-to-end actions, not a chatbot",
    desc: "Every step is real action in the real systems — calls placed, faxes parsed, charts updated, providers notified. Autonomously, end to end.",
    color: "bg-blue-100 text-blue-600 border-blue-200",
  },
  {
    icon: Layers,
    title: "100+ skills + persistent patient memory",
    desc: "Insurance, claims, scheduling, outreach, RCM, clinical ops — plus memory across every interaction so context never gets lost between agents.",
    color: "bg-violet-100 text-violet-600 border-violet-200",
  },
];

/* ── Entry renderers ──────────────────────────────────── */

const ReasoningBlock = ({ entry, round }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35 }}
    className="mx-3 my-1.5 rounded-xl bg-gradient-to-r from-indigo-50 to-blue-50/60 border border-indigo-200/70 px-3.5 py-2.5"
  >
    <div className="flex items-center gap-1.5 mb-1">
      <BrainCircuit className="w-3 h-3 text-indigo-500" />
      <span className="text-[9px] font-bold text-indigo-500 uppercase tracking-[0.12em]">
        ARIA · Reasoning
      </span>
      {round && (
        <span className="ml-auto text-[9px] font-bold text-indigo-400 tabular-nums">
          round {round}
        </span>
      )}
    </div>
    <p className="text-[11.5px] text-indigo-700/90 leading-[1.6]">{entry.text}</p>
  </motion.div>
);

const ActionBlock = ({ entry, isLatest, done }) => {
  const Icon = entry.icon;
  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.35 }}
      className="mx-3 my-1.5 rounded-xl bg-white border-l-[3px] border-slate-200 shadow-sm px-3.5 py-2.5 flex items-center gap-3"
      style={{ borderLeftColor: entry.color }}
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${entry.color}15`, color: entry.color }}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.12em]" style={{ color: entry.color }}>
            Mobilizing
          </span>
          <span className="text-[11.5px] font-bold text-slate-800">{entry.agent}</span>
          <span className="text-[10px] text-slate-400">· {entry.role}</span>
        </div>
        <p className="text-[11px] text-slate-600 leading-snug">{entry.verb}</p>
      </div>
      <div className="flex-shrink-0">
        {isLatest && !done ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: entry.color }} />
        ) : (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
        )}
      </div>
    </motion.div>
  );
};

const ObservationBlock = ({ entry }) => (
  <motion.div
    initial={{ opacity: 0, y: 4 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className="flex items-start gap-2 px-5 py-1"
  >
    <ArrowRight className="w-3 h-3 text-slate-400 flex-shrink-0 mt-[3px]" />
    <span className="text-[11px] text-slate-500 leading-relaxed italic">{entry.text}</span>
  </motion.div>
);

const SuccessBlock = ({ entry }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.97 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4 }}
    className="mx-3 my-2 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 px-3.5 py-2.5 flex items-start gap-2.5"
  >
    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-[1px]" />
    <div>
      <div className="text-[9px] font-bold text-emerald-600 uppercase tracking-[0.12em] mb-0.5">
        Workflow complete
      </div>
      <p className="text-[11.5px] text-emerald-800 leading-snug font-medium">{entry.text}</p>
    </div>
  </motion.div>
);

/* ── Component ────────────────────────────────────────── */

const AshaCortexSlide = () => {
  const [phase, setPhase] = useState("typing"); // typing | streaming | done
  const [typedChars, setTypedChars] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const scrollRef = useRef(null);
  const slideRef = useRef(null);

  // Reset animation every time the slide scrolls into view
  useEffect(() => {
    const el = slideRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPhase("typing");
          setTypedChars(0);
          setVisibleCount(0);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Phase 1: Type the master prompt
  useEffect(() => {
    if (phase !== "typing") return;
    if (typedChars >= MASTER_PROMPT.length) {
      const t = setTimeout(() => setPhase("streaming"), 500);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setTypedChars((c) => Math.min(c + 2, MASTER_PROMPT.length)), TYPING_SPEED);
    return () => clearTimeout(t);
  }, [phase, typedChars]);

  // Phase 2: Stream orchestration entries
  useEffect(() => {
    if (phase !== "streaming") return;
    if (visibleCount >= flowEntries.length) {
      setPhase("done");
      return;
    }
    const t = setTimeout(() => setVisibleCount((c) => c + 1), ENTRY_INTERVAL);
    return () => clearTimeout(t);
  }, [phase, visibleCount]);

  // Auto-scroll when new entries appear
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [visibleCount]);

  const visibleEntries = flowEntries.slice(0, visibleCount);
  const actionCount = flowEntries.slice(0, visibleCount).filter((e) => e.type === "action").length;
  const reasoningCount = flowEntries.slice(0, visibleCount).filter((e) => e.type === "reasoning").length;

  return (
    <div ref={slideRef} className="h-full w-full bg-[#f8faff] flex flex-col relative overflow-hidden font-sans">
      {/* Background */}
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.25, 0.4, 0.25] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-[150px] left-[5%] w-[700px] h-[700px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)" }}
      />
      <motion.div
        animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.35, 0.2] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        className="absolute -bottom-[100px] right-[10%] w-[500px] h-[500px] rounded-full blur-[100px] pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)" }}
      />
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(99,102,241,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.5) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      {/* ═══ TOP: Title ═══ */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 px-10 pt-8 pb-4 flex-shrink-0 text-center"
      >
        <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-2 max-w-[1400px] mx-auto">
          All of our products are powered by{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600">
            ARIA
          </span>
          , the AI brain that unlocks{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600">
            autonomy
          </span>
          .
        </h2>
        <p className="text-lg lg:text-xl text-slate-600 leading-snug max-w-6xl mx-auto">
          ARIA (Asha Reasoning &amp; Intelligence Architecture){" "}
          is our technological breakthrough — unlike most dental AI tools that can only assist your staff (ex: chatbots that answer questions),
          ARIA empowers all 20 of our AI products to{" "}
          <span className="font-semibold text-indigo-600">autonomously take action</span> — completing entire workflows
          end to end.
        </p>
      </motion.div>

      {/* ═══ BODY: Value props + Orchestration graphic ═══ */}
      <div className="flex-1 flex min-h-0 relative z-10">
        {/* Value props */}
        <div className="w-[420px] flex-shrink-0 flex flex-col justify-center pl-10 pr-5 pb-8 gap-5">
          {valueProps.map((prop, i) => {
            const Icon = prop.icon;
            return (
              <motion.div
                key={prop.title}
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}
                className="flex gap-4"
              >
                <div
                  className={`w-11 h-11 rounded-xl ${prop.color} border flex items-center justify-center flex-shrink-0 mt-0.5`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-[16px] font-bold text-slate-800 leading-snug mb-1">{prop.title}</h4>
                  <p className="text-[13px] text-slate-500 leading-relaxed">{prop.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Orchestration graphic */}
        <div className="flex-1 relative pr-10 pb-6 min-h-0 flex flex-col">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.8, ease: "easeOut" }}
            className="relative flex-1 rounded-2xl border border-slate-200/80 bg-white shadow-[0_-4px_60px_rgba(99,102,241,0.08),0_2px_20px_rgba(0,0,0,0.04)] flex flex-col overflow-hidden"
          >
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50/80 flex-shrink-0">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="flex items-center gap-2 px-3 py-0.5 rounded-md bg-white border border-slate-200 shadow-sm">
                  <ShieldCheck className="w-2.5 h-2.5 text-emerald-500" />
                  <span className="text-[10px] text-slate-400 font-medium">app.asha.health</span>
                </div>
              </div>
            </div>

            {/* Master prompt bar */}
            <div className="px-4 py-2.5 border-b border-slate-100 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-sm flex-shrink-0">
                  <BrainCircuit className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-700 font-medium">
                  <span>
                    {MASTER_PROMPT.slice(0, typedChars)}
                    {phase === "typing" && typedChars < MASTER_PROMPT.length && (
                      <motion.span
                        animate={{ opacity: [1, 0] }}
                        transition={{ duration: 0.5, repeat: Infinity }}
                        className="inline-block w-[1px] h-3 bg-indigo-500 ml-[1px] align-middle"
                      />
                    )}
                  </span>
                  <Send className="w-3.5 h-3.5 text-indigo-400 ml-auto flex-shrink-0" />
                </div>
              </div>

              {/* Loop indicator */}
              {phase !== "typing" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-3 mt-2 ml-8"
                >
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200/60">
                    <Repeat className="w-2.5 h-2.5 text-indigo-500" />
                    <span className="text-[9px] font-bold text-indigo-600 uppercase tracking-wider">
                      Reason → Act → Observe
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-500">
                    <span className="font-semibold text-indigo-600 tabular-nums">{reasoningCount}</span> reasoning steps ·{" "}
                    <span className="font-semibold text-blue-600 tabular-nums">{actionCount}</span> agents mobilized
                  </span>
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200/50 ml-auto">
                    <motion.div
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="w-1.5 h-1.5 rounded-full bg-emerald-500"
                    />
                    <span className="text-[8px] font-bold text-emerald-600 tracking-wide">
                      {phase === "done" ? "DONE" : "LIVE"}
                    </span>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Orchestration stream */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto py-2">
              <AnimatePresence initial={false}>
                {visibleEntries.map((entry, i) => {
                  const key = `entry-${i}`;
                  if (entry.type === "reasoning") {
                    return <ReasoningBlock key={key} entry={entry} round={entry.round} />;
                  }
                  if (entry.type === "action") {
                    const isLatest = i === visibleEntries.length - 1;
                    const done = phase === "done";
                    return <ActionBlock key={key} entry={entry} isLatest={isLatest} done={done || !isLatest} />;
                  }
                  if (entry.type === "observation") {
                    return <ObservationBlock key={key} entry={entry} />;
                  }
                  if (entry.type === "success") {
                    return <SuccessBlock key={key} entry={entry} />;
                  }
                  return null;
                })}
              </AnimatePresence>
              {phase === "streaming" && visibleCount < flowEntries.length && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2 py-1 px-5 mt-1"
                >
                  <Loader2 className="w-3 h-3 text-indigo-500 animate-spin" />
                  <span className="text-[10px] text-indigo-500 font-medium">ARIA reasoning...</span>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AshaCortexSlide;
